"use client";

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type { OwnPortalReview } from "../lib/clientPortalApi";
import { PortalReviewStar, portalRatingLabel } from "./PortalReviewRating";
import styles from "./PortalReviewComposer.module.css";

export type PortalReviewDraft = { rating: number; title: string | null; body: string; honeypot?: string };

export function PortalReviewComposer({ ownReview, anonymous = false, active, saving, disabled, error, onSubmit, onCancel }: {
  ownReview: OwnPortalReview | null;
  anonymous?: boolean;
  active: boolean;
  saving: boolean;
  disabled: boolean;
  error: string | null;
  onSubmit: (draft: PortalReviewDraft) => Promise<void>;
  onCancel: () => void;
}) {
  const id = useId();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [rating, setRating] = useState(ownReview?.rating ?? 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState(ownReview?.title ?? "");
  const [body, setBody] = useState(ownReview?.body ?? "");
  const [titleVisible, setTitleVisible] = useState(Boolean(ownReview?.title));
  const [honeypot, setHoneypot] = useState("");

  useEffect(() => {
    if (active && ownReview) bodyRef.current?.focus({ preventScroll: true });
  }, [active, ownReview]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (disabled || rating < 1 || !body.trim()) return;
    void onSubmit({ rating, title: title.trim() || null, body: body.trim(), ...(anonymous ? { honeypot } : {}) });
  }

  return <form className={styles.composer} hidden={!active} onSubmit={submit} aria-label={ownReview ? "Upravit recenzi" : "Napsat recenzi"} aria-busy={saving}>
    <h2 className={styles.heading}>{ownReview ? "Upravit recenzi" : "Napsat recenzi"}</h2>
    {anonymous ? <div className={styles.visuallyHidden} aria-hidden="true"><label htmlFor={`${id}-website`}>Website</label><input id={`${id}-website`} name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(event) => setHoneypot(event.target.value)} /></div> : null}
    <div className={styles.topline}>
      <fieldset className={styles.ratingField} disabled={disabled}>
        <legend className={styles.visuallyHidden}>Vaše hodnocení</legend>
        <div className={styles.ratingOptions} onMouseLeave={() => setHoverRating(0)}>
          {[1, 2, 3, 4, 5].map((value) => <label key={value} className={styles.ratingOption} data-filled={value <= (hoverRating || rating)} onMouseEnter={() => setHoverRating(value)}>
            <input type="radio" name={`${id}-rating`} value={value} checked={rating === value} onChange={() => setRating(value)} aria-label={portalRatingLabel(value)} required />
            <PortalReviewStar />
          </label>)}
        </div>
      </fieldset>
      {!titleVisible ? <button className={styles.titleToggle} type="button" disabled={disabled} aria-expanded={false} onClick={() => setTitleVisible(true)}>Přidat nadpis</button> : null}
    </div>
    {titleVisible ? <input className={styles.titleInput} aria-label="Nadpis recenze (volitelné)" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={255} disabled={disabled} placeholder="Nadpis (volitelné)" /> : null}
    <label className={styles.visuallyHidden} htmlFor={`${id}-body`}>Vaše recenze</label>
    <textarea ref={bodyRef} id={`${id}-body`} className={styles.bodyInput} value={body} onChange={(event) => setBody(event.target.value)} maxLength={5000} rows={3} disabled={disabled} required placeholder="Jaká byla vaše zkušenost? Napište recenzi…" />
    {error ? <p role="alert" className="form-error">{error}</p> : null}
    <div className={styles.footer}>
      <small>{ownReview ? "Úpravu zveřejníme po schválení." : "Recenzi zveřejníme po schválení."}</small>
      <div className={styles.actions}>
        {ownReview ? <button type="button" className={styles.cancel} disabled={saving} onClick={onCancel}>Zrušit</button> : null}
        <button type="submit" className="primary-button" disabled={disabled || rating < 1 || !body.trim()}>{saving ? "Odesílání…" : ownReview ? "Uložit změny" : "Odeslat recenzi"}<span aria-hidden="true">↗</span></button>
      </div>
    </div>
  </form>;
}
